import type { components } from "@contracts/generated/editor-service";

import type { LockReason, SectionConfig, SectionSlug } from "@/api/hooks";
import {
  licensedInventoryVerdict,
  type AdoptionPin,
  type AdoptionPinRequest,
  type AdoptionPreflightItem,
  type AdoptionWriteLane,
  type LibraryAsset,
  type LibraryAssetType,
  type PlacementRegistry,
} from "@/api/library";

import { isLocked, lockReasonOf } from "./fieldState";
import {
  ASSET_TYPE_LABELS,
  assetTypesForSection,
  type LibrarySectionSlug,
} from "./librarySections";
import { LOCK_REASON_DETAIL } from "./labels";

/**
 * What a Project has adopted, as arithmetic rather than as a screen (#372).
 *
 * Everything here is pure, and it is pure for one reason: `PUT
 * /v1/projects/{projectId}/adoptions` replaces the WHOLE pin set, so the
 * sentence a clinician reads before confirming and the array the browser sends
 * have to be two views of one value. Computing them separately is how a surface
 * says "one pin moves, the other four are written back unchanged" and then
 * sends a body holding one pin — which is the same silent unpin the endpoint's
 * own contract text warns about, arrived at through honest-looking code.
 *
 * So `adoptionImpact` returns BOTH: the statement's parts and the `items` the
 * replacement carries. Nothing else builds a request body.
 *
 * WHAT THIS MODULE MAY NOT DO. It may not decide whether something is locked.
 * Lock state is the service's — `FieldState.lock` from the section projection,
 * and `AssetCapabilities.lockedFloor` from the catalog — and the two functions
 * below that touch locks only READ those reports and resolve a disagreement
 * toward locked, exactly as `fieldState.fieldProvenance` does. There is no
 * browser-side rule here that could make something locked, and no cached mirror
 * of the pin set: every function takes the set it was given.
 */

/** One clinician's decision about ONE adoption, before it becomes a request. */
export type AdoptionDecision =
  | { readonly mode: "pin"; readonly assetId: string; readonly version: string }
  | { readonly mode: "unpin"; readonly assetId: string };

/**
 * The whole consequence of one decision against one pin set — the impact
 * statement and the replacement body, from a single computation.
 */
export type AdoptionImpact = {
  readonly decision: AdoptionDecision;
  /** The exact version this project pins on the decision's asset today. */
  readonly from: string | null;
  /** The exact version it will pin, or `null` when the pin is removed. */
  readonly to: string | null;
  /**
   * A pin on a DIFFERENT asset that this decision replaces, because the service
   * caps that asset type at one pin per project.
   */
  readonly displaced: AdoptionPin | null;
  /**
   * Pins on OTHER assets this decision replaces because the SERVER decided the
   * incoming pin owns the same runtime placement as each of them
   * (`placement.ownership_conflict`, read from the preflight or from the
   * write's own refusal). Named here so the sentence a clinician confirms and
   * the set the request omits are one value; empty until the server has said so.
   */
  readonly replacedOwners: readonly AdoptionPin[];
  /** Every pin written back exactly as it was read. */
  readonly unchanged: readonly AdoptionPin[];
  /** The whole set the replacement sends. */
  readonly items: readonly AdoptionPinRequest[];
};

/**
 * How many exact pins of one family a project may hold, from the SERVER (#634).
 *
 * `null` means the registry states no bound — and, identically for every caller
 * here, that the document has not been read yet. Both are "this module cannot
 * say a replacement displaces anything", which is the only claim it makes from
 * this value; the callers that must not act on an unread policy ask
 * `adoptionPolicyReady` instead, because a boolean cannot carry the difference
 * and a surface that guessed would be back where this function started.
 *
 * ## WHY THIS IS A FETCH AND NOT A LIST
 *
 * It used to be `SINGLE_PIN_ASSET_TYPES = ["alert_matrix", "main_concern"]` — a
 * browser-side restatement of `placement_registry._CARDINALITY`, kept in step by
 * hand. It had already fallen out of step: #635 bounded `language_rules` at one
 * adopted pin and the list never learned, so pinning a second Rules asset
 * offered no displacement, sent both pins, and was refused by the server with
 * `placement.cardinality_exceeded` — the dead end the audit recorded for other
 * families, reintroduced by a client-side policy copy.
 *
 * The ratified contract §2.1 settles it: "Asset placement, allowed parent/child
 * relationships, cardinality, and family-specific constraints are server-owned.
 * Clients discover them through a versioned placement/policy registry." So this
 * reads `families[].cardinality.adopted.max` off `GET
 * /v1/library/placement-registry`, exactly as `requiredLanguagesFor` reads the
 * language rule beside it, and a bound added or changed server-side reaches the
 * browser without anyone remembering to edit a list.
 *
 * THE ADOPTED AXIS, never the active one. `cardinality.active` is a bound on
 * what the pinned RUNTIME may reach, decided against a project's own
 * registration — a fact no catalog read can answer, and one this module has no
 * business inferring. `main_concern` carries both and they refuse for different
 * reasons at different boundaries; presenting the active bound as if it were a
 * pin count is precisely the misattribution the registry's own docstrings warn
 * against.
 */
export function adoptedMaxFor(
  registry: PlacementRegistry | undefined,
  assetType: LibraryAssetType,
): number | null {
  const family = registry?.families.find(
    (entry) => entry.assetType === assetType,
  );
  return family?.cardinality.adopted?.max ?? null;
}

/**
 * The families this deployment bounds at exactly one pin, as a sentence.
 *
 * Named from `ASSET_TYPE_LABELS` and ordered as the registry publishes them, so
 * the sentence a clinician reads before a replacing write is a reading of the
 * served policy rather than a paraphrase of a remembered one. `null` while the
 * document is unread: there is no honest sentence to show, and the surfaces that
 * would show it are the same ones `adoptionPolicyReady` holds closed.
 */
export function singlePinSentence(
  registry: PlacementRegistry | undefined,
): string | null {
  if (registry === undefined) {
    return null;
  }
  const bounded = registry.families
    .filter((family) => family.cardinality.adopted?.max === 1)
    .map((family) => ASSET_TYPE_LABELS[family.assetType]);
  if (bounded.length === 0) {
    return null;
  }
  const names =
    bounded.length === 1
      ? bounded[0]
      : `${bounded.slice(0, -1).join(", ")} and ${bounded[bounded.length - 1]}`;
  return `This deployment's placement registry (${registry.version}) bounds a project at one pin for ${names}. The Editor Service decides the whole set before it writes, so a second pin is refused and nothing changes.`;
}

/**
 * Whether a decision of this KIND may be acted on under the policy read's state.
 *
 * TWO ANSWERS, AND THE DIFFERENCE IS THE POINT.
 *
 * A `pin` needs the registry: without it the surface cannot say which pin the
 * replacement displaces, and a confirmation that quietly omitted a displacement
 * it could not see would be describing one write and performing another — the
 * failure `adoptionImpact` exists to make impossible.
 *
 * An `unpin` never needs it. Removal is the recovery path out of every refused,
 * unplaceable or uncomposable state (#612, and `MC-PRJ-07` before it), and the
 * service accepts it unconditionally — an archived asset's pin, an unreachable
 * pin, a pin whose family this registry no longer places. Gating removal on a
 * policy document would make a registry outage recreate the audited dead end
 * exactly, which is why this predicate takes the mode rather than answering once
 * for both.
 */
export function adoptionPolicyReady(
  registry: { readonly isSuccess: boolean },
  mode: AdoptionDecision["mode"],
): boolean {
  return mode === "unpin" || registry.isSuccess;
}

/**
 * The service's own archived-adoption rule, presented rather than re-derived.
 *
 * `PUT /adoptions` keeps an archived asset's existing pins and allows them to be
 * removed, and refuses one added or moved with a 409. Removal staying legal is
 * why unpin is not refused below.
 */
export const ARCHIVED_SENTENCE =
  "This asset is archived. An archived asset's existing pins may be kept or removed, but not added or moved.";

/**
 * The registry's own answer for a clinician-authored Safety track, presented
 * rather than re-derived (#636).
 *
 * Authoring beside the floor is supported (#35) — creating, versioning and
 * archiving such a track all work. What no runtime supports yet is USING one:
 * the pinned engine refuses safety-namespaced files in any overlay, so the
 * placement registry answers `unsupported` / `placement.family_protected_floor`
 * for every authored `safety_track` version, and the adoption preflight (#632)
 * refuses the pin before mutation. A control whose single outcome is that
 * refusal is the affordance ADR 0001 forbids — which is exactly what this
 * sentence exists to disable, honestly, before the click.
 *
 * THE TRIGGER READS ONLY SERVER-SERVED FACTS. For this one family the
 * registry's verdict is a pure function of catalog fields every row carries:
 * seeded rows (the locked floor included) verdict `protected` and stay
 * pinnable; an authored row — `safety_track`, not seeded, not the locked
 * floor — verdicts `unsupported`, deterministically
 * (`placement_registry._catalog_verdict`, pinned by the service's own tests).
 * The browser is not restating policy; it is reading the same inputs the
 * server does, for the one family where they decide alone. Removal stays
 * enabled below for the same reason it does everywhere: an already-stored pin's
 * removal is always writable, and it is the recovery path (#612).
 *
 * Additive Safety extensions that CAN compose are deferred to the Brain
 * extension surface (ruling of 2026-08-31); when that lands, the registry's
 * verdict changes and this sentence retires with it.
 */
export const AUTHORED_SAFETY_SENTENCE =
  "An authored Safety track cannot be adopted yet: the runtime refuses safety files in any overlay, so only the inherited safety floor can run. Additive Safety extensions arrive with the planned Brain extension surface.";

/**
 * The served cardinality policy has not been read, so no pin may be offered.
 *
 * STATED, NOT HIDDEN, and refusing the ADD/MOVE half only (#634). A pin on a
 * family the server bounds at one is a REPLACEMENT, and the confirmation has to
 * say which pin it displaces before it writes — a question only
 * `families[].cardinality.adopted.max` answers. Offering a control whose
 * confirmation would then have to fail closed is the affordance ADR 0001
 * forbids, and it is the drift this ticket removed a hardcoded list to avoid:
 * a browser that assumed a bound would be back to carrying its own policy.
 *
 * REMOVAL IS NEVER REFUSED FOR THIS, here or in the dialog. An un-pin is the
 * recovery path out of every refused, unplaceable or uncomposable state (#612,
 * `MC-PRJ-07`), the service accepts it unconditionally, and none of its inputs
 * is this document. A policy outage that took the un-pin away would rebuild the
 * audited dead end out of an unrelated read.
 */
export const POLICY_UNREAD_SENTENCE =
  "The server-owned placement policy could not be read, so this surface cannot say what adopting here would replace. Removing an existing pin is unaffected.";

/**
 * The service's own inventory verdict, presented rather than re-derived (#638).
 *
 * Keyed on `AssetCapabilities.licensedInventory.reason`, which the catalog read
 * computes from the seed catalog's licensing declarations — the SAME derivation
 * `placement_registry.catalog_verdict` refuses on, so the sentence a clinician
 * reads and the refusal a write would return are one answer from one authority.
 *
 * THERE IS NO CLIENT-SIDE LICENSING RULE HERE, and that is the ticket's own
 * prohibition rather than a style preference. The browser holds no roster of
 * approved instruments, checks no names, and cannot make a row licensed or
 * unlicensed; it renders a verdict.
 *
 * THE TWO UNKNOWNS GO OPPOSITE WAYS, deliberately. An unknown REASON on an
 * `excluded` verdict still refuses, with generic copy: the server has said this
 * row is out, and only the wording is missing. An unknown STATE — or a response
 * with no verdict at all, which a service older than this bundle can serve
 * mid-rollout — refuses NOTHING here, because this function's refusals are
 * predictions of what the write will do, and predicting a refusal the server
 * has not made would disable a legitimate control on every family at once. The
 * write is the gate; `placement_registry.catalog_verdict` refuses there.
 *
 * REMOVAL IS NEVER REFUSED, exactly as for archive: an already-stored pin's
 * removal is always writable and is the recovery path (#612).
 */
export const UNLICENSED_SENTENCE: Record<
  components["schemas"]["LicensedInventoryRefusalReason"],
  string
> = {
  not_seeded_inventory:
    "This instrument is not part of the approved clinical inventory, so it cannot be adopted. Approved instruments are seeded from the clinical catalog; nothing authored here can join it.",
  license_withdrawn:
    "This instrument is no longer licensed for new adoption. Projects that already pin it keep running it at their exact pinned version, and the pin can still be inspected or removed.",
};

const UNLICENSED_FALLBACK =
  "This instrument is outside the approved clinical inventory, so it cannot be newly adopted.";

/**
 * Why a NEW adoption of this asset is refused by the inventory, or `null`.
 *
 * `null` for every ungoverned family, which is what `not_governed` means on the
 * wire — an answer, not an absence.
 */
export function unlicensedRefusal(asset: LibraryAsset): string | null {
  const verdict = licensedInventoryVerdict(asset);
  if (verdict?.state !== "excluded") {
    return null;
  }
  return UNLICENSED_SENTENCE[verdict.reason] ?? UNLICENSED_FALLBACK;
}

/**
 * A pin the catalog cannot name.
 *
 * The catalog is read with `includeArchived`, so this is not the archived case:
 * it is a pin whose asset the caller cannot see at all. Naming a version to
 * move it to, or stating what removing it would cost, both need the row — so
 * the surface says what it holds and offers nothing it cannot describe.
 */
export const UNKNOWN_ASSET_SENTENCE =
  "The shared library did not return this asset, so this adoption cannot be described or changed here.";

function assetById(
  catalog: readonly LibraryAsset[],
  assetId: string,
): LibraryAsset | undefined {
  return catalog.find((asset) => asset.id === assetId);
}

/**
 * The pin a new pin on `assetId` would displace, or `null`.
 *
 * Only a family the SERVER bounds at exactly one displaces anything: pinning a
 * second questionnaire adds one, pinning a second Main concern replaces the
 * first. A move of this project's own pin is not a displacement — it is the
 * `from`/`to` pair — so the asset's own pin is excluded here and accounted for
 * by `adoptionImpact`.
 *
 * EXACTLY ONE, never "at or over the bound". A family bounded at three has no
 * answer to which of the three a fourth pin should replace, and inventing one
 * would be a browser deciding clinical content on no authority. The server
 * refuses that set instead — `placement.cardinality_exceeded`, carrying every
 * refused item — and the dialog renders the refusal. Only `max === 1` makes the
 * displaced pin derivable, which is why it is the only case treated as a
 * replacement.
 */
export function displacedPin(
  assetId: string,
  current: readonly AdoptionPin[],
  catalog: readonly LibraryAsset[],
  registry: PlacementRegistry | undefined,
): AdoptionPin | null {
  const incoming = assetById(catalog, assetId);
  if (incoming === undefined || adoptedMaxFor(registry, incoming.assetType) !== 1) {
    return null;
  }
  return (
    current.find(
      (pin) =>
        pin.assetId !== assetId &&
        assetById(catalog, pin.assetId)?.assetType === incoming.assetType,
    ) ?? null
  );
}

/**
 * One decision against one pin set: what changes, what does not, and the whole
 * set that says so.
 *
 * `items` is deliberately built from `unchanged` rather than from `current`,
 * so the array the request carries and the count the sentence quotes are the
 * same list. They cannot drift, because there is only one of them.
 */
/**
 * The request shape of one pin: `{assetId, version}` and nothing else.
 *
 * `AdoptionPin` as the service REPORTS it also carries `assetType`, joined from
 * the catalog so the pin set can address its own library routes (#615). The
 * service owns that fact and refuses to be told it — a request pin echoing
 * `assetType` is a 422 under `extra="forbid"` — so every `PUT /adoptions` body
 * is built through this projection rather than by spreading response pins.
 */
export function pinRequest(
  pin: Pick<AdoptionPin, "assetId" | "version">,
): AdoptionPinRequest {
  return { assetId: pin.assetId, version: pin.version };
}

export function adoptionImpact(
  decision: AdoptionDecision,
  current: readonly AdoptionPin[],
  catalog: readonly LibraryAsset[],
  registry: PlacementRegistry | undefined,
  /**
   * Asset ids the SERVER named as owners of the incoming pin's runtime
   * placement (`ownershipConflicts`), to be replaced by it. Only ids the
   * project actually pins are honoured — the browser never invents an owner —
   * and a removal replaces nothing.
   */
  replacedOwnerIds: readonly string[] = [],
): AdoptionImpact {
  const held = current.find((pin) => pin.assetId === decision.assetId) ?? null;
  const displaced =
    decision.mode === "pin"
      ? displacedPin(decision.assetId, current, catalog, registry)
      : null;
  const replacedOwners =
    decision.mode === "pin"
      ? current.filter(
          (pin) =>
            pin.assetId !== decision.assetId &&
            pin.assetId !== displaced?.assetId &&
            replacedOwnerIds.includes(pin.assetId),
        )
      : [];
  const replaced = new Set(
    [
      decision.assetId,
      displaced?.assetId,
      ...replacedOwners.map((pin) => pin.assetId),
    ].filter((id): id is string => id !== undefined),
  );
  const unchanged = current.filter((pin) => !replaced.has(pin.assetId));

  return {
    decision,
    from: held?.version ?? null,
    to: decision.mode === "pin" ? decision.version : null,
    displaced,
    replacedOwners,
    unchanged,
    items:
      decision.mode === "pin"
        ? [
            ...unchanged.map(pinRequest),
            { assetId: decision.assetId, version: decision.version },
          ]
        : unchanged.map(pinRequest),
  };
}

/**
 * What the effective configuration says one adoption contributed, and whether
 * the service reported it locked.
 */
export type AdoptionProvenance = {
  /** Sections whose effective values this adoption supplied, in contract order. */
  readonly sections: readonly SectionSlug[];
  /** The lock the service reported on those values, when it reported one. */
  readonly lock: LockReason | null;
};

/**
 * Attribution and lock state per adopted asset, read off the section
 * projection.
 *
 * The join is the CONTRACT's, not this module's. `FieldState.origin` is
 * `{kind: "library_adoption", ref: <assetId>, version}` — the projection
 * attributes an effective value to the pin that supplied it, and it keeps that
 * attribution on a LOCKED field only where the value's source really is that
 * layer, including a suppressed locked-floor pin whose whole meaning is "this
 * project adopts the base safety canon". So a lock arriving here was decided by
 * the engine and merely carried; nothing below infers one.
 *
 * `lockReasonOf` and `isLocked` come from `lib/fieldState.ts` rather than being
 * spelled again, for the reason that module exists: one reconciliation of the
 * contract's editorial state, not one per surface.
 */
export function adoptionProvenanceByAsset(
  sections: readonly SectionConfig[],
): ReadonlyMap<string, AdoptionProvenance> {
  const found = new Map<string, { sections: SectionSlug[]; lock: LockReason | null }>();

  for (const section of sections) {
    for (const field of section.fields) {
      const { origin } = field;
      if (!origin || origin.kind !== "library_adoption") {
        continue;
      }
      const entry = found.get(origin.ref) ?? { sections: [], lock: null };
      if (!entry.sections.includes(section.section)) {
        entry.sections.push(section.section);
      }
      // First reported lock wins, and any lock wins over none: ADR 0001 has no
      // "unless another field of the same adoption was editable" clause.
      entry.lock ??= isLocked(field) ? (lockReasonOf(field) ?? null) : null;
      found.set(origin.ref, entry);
    }
  }

  return found;
}

/**
 * Whether the SERVER considers this adoption locked, and why — or `null`.
 *
 * Two independent server reports, resolved toward locked, exactly as
 * `fieldProvenance` resolves a lock/provenance disagreement:
 *
 *  1. the section projection's engine-reported lock on values this adoption
 *     supplied, which needs a mounted projection engine to exist at all;
 *  2. `AssetCapabilities.lockedFloor`, the catalog's own declaration that the
 *     asset IS the clinical guard floor — derived server-side, never stored,
 *     and available whether or not an engine is mounted.
 *
 * The second is why an unreadable projection does not silently unlock a floor.
 * It is also all a caller gets in a deployment with no engine mount, which is
 * why the surface says so rather than implying the projection agreed.
 */
export function adoptionLock(
  asset: LibraryAsset | undefined,
  provenance: AdoptionProvenance | undefined,
): LockReason | null {
  if (provenance?.lock) {
    return provenance.lock;
  }
  return asset?.capabilities.lockedFloor === true ? "safety_floor" : null;
}

/**
 * Why this adoption cannot be pinned, moved or removed, or `null` when it can.
 *
 * ONE derivation for every write control on the surface, ordered the way
 * `MatrixPinning.adoptionRefusal` orders its two: the missing capability first,
 * because a caller without `draft.write` is refused whatever the asset's state
 * would have allowed. Archived refuses only what the service refuses: an added
 * or moved pin, never a removed one.
 *
 * A LOCK REFUSES WHAT WOULD WEAKEN THE FLOOR, WHICH IS NOT EVERY WRITE. ADR
 * 0001 requires locked content to disable its controls, and a safety floor a
 * clinician can remove — or move off the version the library publishes — is not
 * a floor. So a lock refuses `unpin`, and refuses `pin` on an asset this
 * project ALREADY holds, which is a move of an existing pin.
 *
 * It does NOT refuse adopting one. This ordering used to put the lock ahead of
 * the action entirely, which made an ADD refuse too, and that was wrong three
 * times over: adopting the base safety canon does not weaken it;
 * `library.replace_project_adoptions` accepts the write, refusing only an
 * unknown asset, an unknown version, a second capped pin and an archived add or
 * move, so the browser was refusing something the service allows; and the
 * sentence it showed — `LOCK_REASON_DETAIL.safety_floor`, about weakening —
 * described a refusal it was not making. A generic surface that cannot pin one
 * whole asset type is not generic, and the server treats an adopted floor as a
 * first-class state: `adoption_serializer.serialize_adoptions` suppresses a
 * floor pinned at the library's head, because the project then runs the base
 * safety canon.
 *
 * The lock's sentence is `LOCK_REASON_DETAIL`'s, so a locked adoption and a
 * locked field explain themselves in the same words.
 */
export function adoptionRefusal(
  action: "pin" | "unpin",
  {
    asset,
    lock,
    held,
    missingWriteReason,
    policyUnread,
  }: {
    asset: LibraryAsset | undefined;
    lock: LockReason | null;
    /**
     * Whether this project already pins this asset.
     *
     * It is what separates the two things `"pin"` means: on a held asset it is a
     * MOVE, on one the project does not hold it is an ADD.
     */
    held: boolean;
    missingWriteReason: string | null;
    /**
     * Whether the served placement/policy registry has NOT been read (#634).
     *
     * REQUIRED rather than defaulted, and that is the whole design: a surface
     * that could omit it would silently offer a pin whose confirmation cannot
     * describe itself, which is the drift a defaulted argument invites and the
     * class of drift this ticket exists to close. Every caller states its own
     * answer, and a new adoption surface cannot compile without one.
     */
    policyUnread: boolean;
  },
): string | null {
  if (missingWriteReason) {
    return missingWriteReason;
  }
  if (action === "pin" && policyUnread) {
    // Before the asset lookup: without the policy the surface cannot describe
    // ANY pin, including one whose catalog row it happens to hold.
    return POLICY_UNREAD_SENTENCE;
  }
  if (asset === undefined) {
    return UNKNOWN_ASSET_SENTENCE;
  }
  if (lock && (action === "unpin" || held)) {
    return LOCK_REASON_DETAIL[lock];
  }
  // BEFORE the archived arm, for the reason the authored-Safety arm below
  // states: an unlicensed instrument is unadoptable whatever its archive state,
  // and the more fundamental reason is the one to show. Covers ADD and MOVE
  // alike, matching the preflight, which gates both; unpin falls through,
  // because removal is the recovery path.
  const unlicensed = action === "pin" ? unlicensedRefusal(asset) : null;
  if (unlicensed) {
    return unlicensed;
  }
  if (
    action === "pin" &&
    asset.assetType === "safety_track" &&
    !asset.capabilities.seeded &&
    !asset.capabilities.lockedFloor
  ) {
    // Before the archived arm: an authored Safety track is unadoptable
    // regardless of its archive state, and the more fundamental reason is the
    // one to show. Covers ADD and MOVE alike, matching the preflight, which
    // gates both; unpin falls through, because removal is the recovery path.
    return AUTHORED_SAFETY_SENTENCE;
  }
  if (action === "pin" && asset.capabilities.archived) {
    return ARCHIVED_SENTENCE;
  }
  return null;
}

/** Whether a newer immutable version exists than the one this project pins. */
export function isBehind(
  pin: Pick<AdoptionPin, "assetId" | "version">,
  asset: LibraryAsset | undefined,
): boolean {
  return asset !== undefined && pin.version !== asset.latestVersion;
}

/**
 * Which write a decision goes through (#749, #634).
 *
 * `adoptions` is #372's whole-set `PUT /adoptions`. `pathway-activation` is the
 * server-owned operation that adopts a Pathway family or variant AND derives
 * the runtime registration its coordinate needs — the step the #644 finding
 * recorded no product control for. `main-concern-activation` is its
 * Main-concern analogue: the operation that adopts the project's single Main
 * concern AND registers its coordinate as the runtime's opening evaluation, or
 * reverts that registration to the base pack's on removal. A decision about
 * either family cannot go through the first: an authored coordinate is refused
 * there before the write (`placement.runtime_unreachable`) and nothing in the
 * browser may write the registration that would make it reachable (ratified
 * §2.1).
 *
 * THE FAMILY IS THE SERVER'S FACT, read in the order the server states it: the
 * held pin's own `assetType` first — joined server-side from the catalog row
 * and consistent by the composite FK — then the catalog row for an asset this
 * project does not pin yet. Nothing here infers a family from where a control
 * was rendered.
 */
export type AdoptionLane = AdoptionWriteLane;

/** The lanes that register a runtime coordinate with the pin. */
export type ActivationLane = Exclude<AdoptionLane, "adoptions">;

/** The two families `POST /pathway-activation` activates. */
export function isPathwayFamily(
  assetType: LibraryAssetType | null | undefined,
): boolean {
  return assetType === "pathway" || assetType === "pathway_variant";
}

/** The one family `POST /main-concern-activation` activates. */
export function isMainConcernFamily(
  assetType: LibraryAssetType | null | undefined,
): boolean {
  return assetType === "main_concern";
}

/**

 * The one family `POST /closing-activation` activates (#866's route, #867's
 * control).
 *
 * Its registration is a PAIR — `track_states.conclusion` and `conclusion_track`
 * — which the pinned engine refuses to load with only one half present, so this
 * family can never take the whole-set replacement: that route may not touch the
 * draft, and a Closing pin whose enabler keys the project does not carry is
 * refused by the preflight before anything is written.
 */
export function isClosingConversationFamily(
  assetType: LibraryAssetType | null | undefined,
): boolean {
  return assetType === "closing_conversation";
}

/**
 * The two families `POST /background-activation` activates (#858, wired #914).
 *
 * BOTH, and that is the operation's own shape rather than a grouping choice:
 * the registration it derives is a fact about the two together —
 * `rules.bg_variant` is the profile's coordinate and `rules.bg_categories` is
 * the domains' roster — and the coherence rule that refuses them
 * (`background_profile.variant_incoherences`) is stated over the pair. A lane
 * owning only one of them would write half a registration and hand the other
 * half to a client to keep in step.
 */
export function isBackgroundFamily(
  assetType: LibraryAssetType | null | undefined,
): boolean {
  return assetType === "background_set" || assetType === "background_profile";
}

/**
 * The lane one FAMILY's decisions take, from the family alone.
 *
 * `adoptions` for every family that registers nothing — the whole-set
 * replacement is still the one route for those — and the family's own
 * activation for the four that do. ONE map, read by both the decision dialog
 * (to choose the writer) and the section block (to say which write a control
 * performs), so the sentence a clinician reads and the request the confirm
 * sends cannot describe different operations.
 */
export function activationLaneFor(
  assetType: LibraryAssetType | null | undefined,
): AdoptionLane {
  if (isPathwayFamily(assetType)) {
    return "pathway-activation";
  }
  if (isMainConcernFamily(assetType)) {
    return "main-concern-activation";
  }
  if (isClosingConversationFamily(assetType)) {
    return "closing-activation";
  }
  if (isBackgroundFamily(assetType)) {
    return "background-activation";
  }
  return "adoptions";
}

/** Whether this asset's family is one the given activation lane writes for. */
export function isActivationMember(
  lane: ActivationLane,
  assetType: LibraryAssetType | null | undefined,
): boolean {
  return activationLaneFor(assetType) === lane;
}

/**
 * The activation lane a library SECTION's adoptions take, or `adoptions`.
 *
 * Derived from the section's own adoptable families through
 * `SECTION_BY_ASSET_TYPE` — never from the rail slug or the response shape —
 * so a section whose families all activate says so, and a section holding any
 * family that replaces stays on the whole-set replacement. That is what lets
 * the adopt-new list say, before the click, which write a `Pin …` performs.
 */
export function activationLaneForSection(
  section: LibrarySectionSlug,
): AdoptionLane {
  const lanes = new Set(
    assetTypesForSection(section).map((assetType) =>
      activationLaneFor(assetType),
    ),
  );
  if (lanes.size !== 1) {
    return "adoptions";
  }
  return [...lanes][0] ?? "adoptions";
}

/**
 * One asset's family as the server states it, or `null` when neither the pin
 * set nor the catalog names it.
 */
export function adoptionFamilyOf(
  assetId: string,
  current: readonly AdoptionPin[],
  catalog: readonly LibraryAsset[],
): LibraryAssetType | null {
  const held = current.find((pin) => pin.assetId === assetId);
  return held?.assetType ?? assetById(catalog, assetId)?.assetType ?? null;
}

export function adoptionLane(
  decision: AdoptionDecision,
  current: readonly AdoptionPin[],
  catalog: readonly LibraryAsset[],
): AdoptionLane {
  return activationLaneFor(adoptionFamilyOf(decision.assetId, current, catalog));
}

/**
 * An activation body and its consequence, from the SAME impact the sentence
 * is read from (#749, #634).
 *
 * `items` is the complete pin set OF THE LANE'S FAMILIES the project should
 * hold after the decision — every such pin `adoptionImpact` kept, plus the one
 * it adds, or minus the one it removes and the one it displaces — and nothing
 * of any other family. Those other pins are `preserved`: the Editor Service
 * carries them through unchanged under the same whole-set token, so they are
 * named in the confirmation and NOT sent. The body never carries a coordinate,
 * a registration or a mode: the server derives all of them from the exact pins.
 *
 * For the Main-concern lane the displacement `adoptionImpact` already applied
 * is what makes `items` at most one pin: the served bound is one adopted Main
 * concern, so the pin being replaced is dropped rather than carried beside the
 * new one — the implicit merge ratified §2.2 forbids never leaves the browser.
 */
export type ActivationImpact = {
  readonly items: readonly AdoptionPinRequest[];
  readonly preserved: readonly AdoptionPin[];
};

/** #749's name for the Pathway lane's impact, kept for its callers. */
export type PathwayActivationImpact = ActivationImpact;

export function activationImpact(
  impact: AdoptionImpact,
  current: readonly AdoptionPin[],
  catalog: readonly LibraryAsset[],
  lane: ActivationLane,
): ActivationImpact {
  return {
    items: impact.items.filter((pin) =>
      isActivationMember(lane, adoptionFamilyOf(pin.assetId, current, catalog)),
    ),
    preserved: impact.unchanged.filter(
      (pin) =>
        !isActivationMember(lane, adoptionFamilyOf(pin.assetId, current, catalog)),
    ),
  };
}

export function pathwayActivationImpact(
  impact: AdoptionImpact,
  current: readonly AdoptionPin[],
  catalog: readonly LibraryAsset[],
): PathwayActivationImpact {
  return activationImpact(impact, current, catalog, "pathway-activation");
}

/**
 * What the SERVER said one pin decision would replace, read off a preflight's
 * items or a write refusal's — the one place the ownership rule reaches the
 * browser (`placement.ownership_conflict` + `ownershipConflicts`).
 *
 * THREE ANSWERS, AND THE THIRD IS THE POINT.
 *
 * `none` — the server refused nothing about ownership: the decision proceeds
 * as the catalog described it. `replace` — the incoming pin was refused for
 * owning the same runtime placement as pins this project HOLDS, every named
 * owner is one of them, and nothing else in the set was refused; the dialog
 * states the replacement and sends the set that omits those owners.
 * `unresolved` — the server refused on ownership but the plan is not
 * unambiguous from here: an owner it named is not a pin this project holds
 * (so removing it is not this write's to do), it named none, or another item
 * was refused beside it. The dialog then refuses safely and names what has to
 * be resolved by hand.
 *
 * NOTHING HERE KNOWS WHICH FAMILIES CONFLICT, WHICH COORDINATES COLLIDE OR
 * WHICH PACK PATHS ARE CLAIMED. Placement, manifest parsing and ownership are
 * the Editor Service's (ratified §2.1); this reads a verdict and asset ids.
 */
export type ReplacementPlan =
  | { readonly kind: "none" }
  | { readonly kind: "replace"; readonly incumbents: readonly AdoptionPin[] }
  | { readonly kind: "unresolved"; readonly conflicts: readonly string[] };

const OWNERSHIP_CONFLICT = "placement.ownership_conflict";

export function replacementPlan(
  decision: AdoptionDecision,
  items: readonly AdoptionPreflightItem[],
  current: readonly AdoptionPin[],
): ReplacementPlan {
  if (decision.mode !== "pin") {
    return { kind: "none" };
  }
  const subject = items.find((item) => item.assetId === decision.assetId);
  if (
    subject === undefined ||
    subject.decision !== "refuse" ||
    subject.reason !== OWNERSHIP_CONFLICT
  ) {
    return { kind: "none" };
  }
  const conflicts = subject.ownershipConflicts ?? [];
  const otherRefusals = items.some(
    (item) => item.assetId !== decision.assetId && item.decision === "refuse",
  );
  const incumbents = conflicts.map((assetId) =>
    current.find((pin) => pin.assetId === assetId && pin.assetId !== decision.assetId),
  );
  if (
    conflicts.length === 0 ||
    otherRefusals ||
    incumbents.some((pin) => pin === undefined)
  ) {
    return { kind: "unresolved", conflicts };
  }
  return {
    kind: "replace",
    incumbents: incumbents.filter((pin): pin is AdoptionPin => pin !== undefined),
  };
}
